import { useState, useEffect } from "react";
import { X, Upload, Loader } from "lucide-react";




export default function EditCategoryModal({
  isOpen,
  category,
  onClose,
  onSave,
  unitMeasurements = [],
}) {
  const [formData, setFormData] = useState({
    name: "",
    unitMeasurement: "",
    customUnitMeasurement: "",
    image: null,
    imagePreview: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Populate form when category changes
  useEffect(() => {
    if (!category) return;

    const isCustomUnit = !unitMeasurements.includes(category.unitMeasurement);

    setFormData({
      name: category.name || "",
      unitMeasurement: isCustomUnit ? "Custom" : category.unitMeasurement || "",
      customUnitMeasurement: isCustomUnit
        ? category.unitMeasurement
        : category.customUnitMeasurement || "",
      image: null,
      imagePreview: category.image || null,
    });
  }, [category, unitMeasurements]);


  if (!isOpen || !category) return null;

  const handleImageUpload = (file) => {
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({
          ...formData,
          image: file,
          imagePreview: reader.result,
        });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDeleteImage = () => {
    setFormData({
      ...formData,
      image: null,
      imagePreview: null,
    });
  };

  const handleSave = async () => {
    const finalUnitMeasurement =
      formData.unitMeasurement === "Custom"
        ? formData.customUnitMeasurement
        : formData.unitMeasurement;

    if (!formData.name.trim() || !finalUnitMeasurement.trim()) return;

    try {
      setIsSubmitting(true); // disable button + show spinner
      await onSave({
        ...category,
        name: formData.name.trim(),
        unitMeasurement: finalUnitMeasurement,
        customUnitMeasurement:
          formData.unitMeasurement === "Custom"
            ? formData.customUnitMeasurement
            : "",
        image: formData.image,
      });
    } catch (err) {
      console.error("Failed to update category:", err);
    } finally {
      setIsSubmitting(false); // re-enable button
    }
  };

  const isFormValid = () => {
    if (!formData.name.trim()) return false;
    if (formData.unitMeasurement === "") return false;
    if (
      formData.unitMeasurement === "Custom" &&
      !formData.customUnitMeasurement.trim()
    ) {
      return false;
    }
    return true;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-[#1E1E1E] rounded-2xl w-full max-w-md p-4 md:p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg md:text-xl font-bold text-[#111111] dark:text-white font-poppins">
            Edit Category
          </h3>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors"
          >
            <X size={18} className="text-[#666666] dark:text-[#A0A0A0]" />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[#111111] dark:text-white font-inter mb-2">
              Category Name *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) =>
                setFormData({ ...formData, name: e.target.value })
              }
              placeholder="e.g., Chairs, Tables"
              className="w-full px-4 py-2.5 rounded-lg border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#262626] text-[#111111] dark:text-white placeholder-[#666666] dark:placeholder-[#A0A0A0] focus:outline-none focus:ring-2 focus:ring-[#62397f] dark:focus:ring-[#7C3AED] transition-all text-sm font-inter"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#111111] dark:text-white font-inter mb-2">
              Unit Measurement *
            </label>
            <select
              value={formData.unitMeasurement}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  unitMeasurement: e.target.value,
                  customUnitMeasurement:
                    e.target.value !== "Custom"
                      ? ""
                      : formData.customUnitMeasurement,
                });
              }}
              className="w-full px-4 py-2.5 rounded-lg border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#262626] text-[#111111] dark:text-white focus:outline-none focus:ring-2 focus:ring-[#62397f] dark:focus:ring-[#7C3AED] transition-all text-sm font-inter"
            >
              <option value="">Select unit measurement</option>
              {unitMeasurements.map((unit) => (
                <option key={unit} value={unit}>
                  {unit}
                </option>
              ))}
            </select>
          </div>

          {/* Custom Unit Measurement Input - Conditional */}
          {formData.unitMeasurement === "Custom" && (
            <div
              className="overflow-hidden transition-all duration-300 ease-in-out"
              style={{
                animation: "slideDown 0.3s ease-out",
              }}
            >
              <label className="block text-sm font-medium text-[#111111] dark:text-white font-inter mb-2">
                Custom Unit Measurement *
              </label>
              <input
                type="text"
                value={formData.customUnitMeasurement}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    customUnitMeasurement: e.target.value,
                  })
                }
                placeholder="e.g., Per Bundle, Per Package"
                autoFocus
                className="w-full px-4 py-2.5 rounded-lg border border-[#E5E7EB] dark:border-[#2A2A2A] bg-white dark:bg-[#262626] text-[#111111] dark:text-white placeholder-[#666666] dark:placeholder-[#A0A0A0] focus:outline-none focus:ring-2 focus:ring-[#62397f] dark:focus:ring-[#7C3AED] transition-all text-sm font-inter"
              />
            </div>
          )}

          {/* Image Upload */}
          <div>
            <label className="block text-sm font-medium text-[#111111] dark:text-white font-inter mb-2">
              Category Image *
            </label>
            {formData.imagePreview ? (
              <div className="relative">
                <img
                  src={formData.imagePreview}
                  alt="Preview"
                  className="w-full h-40 object-cover rounded-lg border border-[#E5E7EB] dark:border-[#2A2A2A]"
                />
                <button
                  onClick={handleDeleteImage}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-all duration-200 hover:scale-110 active:scale-95 shadow-lg"
                  title="Delete image"
                >
                  <X size={16} />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-[#E5E7EB] dark:border-[#2A2A2A] rounded-lg cursor-pointer hover:bg-[#F9FAFB] dark:hover:bg-[#262626] transition-colors">
                <Upload
                  size={24}
                  className="text-[#666666] dark:text-[#A0A0A0] mb-2"
                />
                <span className="text-sm text-[#666666] dark:text-[#A0A0A0] font-inter mb-1">
                  Click to upload image
                </span>
                <span className="text-xs text-[#999999] dark:text-[#808080] font-inter">
                  PNG, JPG, JPEG, GIF, WEBP (Max 5MB)
                </span>
                <input
                  type="file"
                  accept="image/png,image/jpg,image/jpeg,image/gif,image/webp"
                  onChange={(e) => handleImageUpload(e.target.files[0])}
                  className="hidden"
                />
              </label>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3 mt-6">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2.5 border border-[#E5E7EB] dark:border-[#2A2A2A] text-[#111111] dark:text-white rounded-lg font-medium text-sm font-inter transition-all hover:bg-[#F9FAFB] dark:hover:bg-[#262626] active:scale-95"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={!isFormValid() || isSubmitting}
            className="flex-1 px-4 py-2.5 bg-gradient-to-r from-[#62397f] to-[#7C3AED] text-white rounded-lg font-semibold text-sm font-inter transition-all hover:shadow-lg hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
          >
            {isSubmitting && <Loader className="animate-spin" size={12} />}
            {isSubmitting ? "Updating..." : "Update Category"}
          </button>
        </div>

        <style jsx="true">{`
          @keyframes slideDown {
            from {
              opacity: 0;
              max-height: 0;
              transform: translateY(-10px);
            }
            to {
              opacity: 1;
              max-height: 500px;
              transform: translateY(0);
            }
          }
        `}</style>
      </div>
    </div>
  );
}
